import React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import CONFIG from 'config'
import { BookContext, ExerciseTransition, GetAstridScoreOptions } from '@astrid/components'
import { exerciseScore, trackExerciseActivity } from 'api/activityApi'
import { astridGetScore } from 'api/astridApi'
import { asyncUpdateProgressEntity, Book, BookMode, BookProgress } from 'store/services/Book'
import { BuildingExerciseData, Exercise, ExerciseType, selectExercisesByCurrentBookId } from 'store/services/Exercise'
import { AppState } from 'store/types'
import { GACategory, GAEvent, trackEvent } from 'utils/analytics'

import { InjectListenButton } from './InjectListenButton'
import './BookFactory.scss'

interface IBookFactoryProps extends ConnectedProps<typeof connector> {
  book: Book
  bookProgress: BookProgress
  bookMode: BookMode
  onCompleteBook: () => void
}

class BookFactory extends React.Component<IBookFactoryProps> {
  private startTimestamp: number = 0

  componentDidMount() {
    this.startTimestamp = Date.now()
  }

  componentWillUnmount() {
    const { book } = this.props
    const timeSpentInBook = ~~((Date.now() - this.startTimestamp) / 1000)
    trackEvent({
      event: GAEvent.BOOK_EXIT,
      category: GACategory.BOOK,
      label: 'Exited the book',
      book_title: book.title,
      time_spent_in_book: timeSpentInBook
    })
  }

  setIndex = (newIndex: number) => {
    const { bookMode, bookProgress } = this.props

    this.props.asyncUpdateProgressEntity({
      index: newIndex,
      progressId: bookProgress._id,
      isCompleted: false,
      bookMode
    })
  }

  goPrev() {
    const { index, indexListening } = this.props.bookProgress
    const { exercises } = this.props.book
    const newIndex = (this.props.bookMode === BookMode.READ ? index : indexListening) - 1

    if (newIndex >= 0 && newIndex < exercises.length - 1) {
      this.setIndex(newIndex)
    }
  }

  goNext = async () => {
    const { bookProgress, bookMode } = this.props
    const currentIndex = bookMode === BookMode.READ ? bookProgress.index : bookProgress.indexListening
    const { exercises } = this.props.book
    const isLastBookExercise = currentIndex >= exercises.length
    if (isLastBookExercise) return

    this.setIndex(currentIndex + 1)
  }

  onNextExercise = (exercisePercentageScore?: number, skipDelay: boolean = false) => {
    const { bookProgress, bookMode } = this.props
    const currentIndex = bookMode === BookMode.READ ? bookProgress.index : bookProgress.indexListening

    const currentExercise = this.props.exercises[currentIndex]
    const { MultiChoiseQuestion, WordBuilder } = ExerciseType
    const { type } = currentExercise

    if ([MultiChoiseQuestion, WordBuilder].includes(type)) {
      exerciseScore(
        exercisePercentageScore!,
        type,
        type === WordBuilder ? ((currentExercise as Exercise).data as BuildingExerciseData).wordOrSentence : undefined
      )
    }

    setTimeout(
      () => {
        this.goNext()
      },
      skipDelay ? 0 : CONFIG.NEXT_EXERCISE_DELAY
    )
  }

  render() {
    const { book, bookProgress, exercises, bookMode } = this.props

    if (
      exercises.length > 0 &&
      ((bookMode === BookMode.READ && bookProgress.index < exercises.length) ||
        (bookMode === BookMode.LISTEN && bookProgress.indexListening < exercises.length))
    ) {
      return (
        <div className="book-factory">
          <ExerciseTransition
            getAstridScore={(opt: GetAstridScoreOptions) => {
              return astridGetScore({ ...opt, bookId: book._id })
            }}
            displayDetailedScore
            astridApiUrl={process.env.REACT_APP_API_URL}
            onCurrentExerciseCompleted={this.onNextExercise}
            trackExerciseActivity={trackExerciseActivity}
            exercises={exercises as any}
            currentIndex={bookMode === BookMode.READ ? bookProgress.index : bookProgress.indexListening}
            bookId={book._id}
            bookContext={bookMode === BookMode.READ ? BookContext.reading : BookContext.listening}
          />
          <InjectListenButton
            key={exercises[bookMode === BookMode.READ ? bookProgress.index : bookProgress.indexListening]._id}
            exercise={exercises[bookMode === BookMode.READ ? bookProgress.index : bookProgress.indexListening]}
          />
        </div>
      )
    } else return null
  }
}

const connector = connect(
  (state: AppState) => ({
    exercises: selectExercisesByCurrentBookId(state)
  }),
  {
    asyncUpdateProgressEntity
  }
)

export default connector(BookFactory)
